/**
 * Persistent cache using localStorage with stale-while-revalidate pattern.
 *
 * Features:
 * - Persists data across page refreshes and browser sessions
 * - Returns stale data immediately while fresh data loads in background
 * - Expires data that is too old (default: 1 hour) to avoid showing very stale data
 * - Falls back to in-memory cache if localStorage is unavailable
 */

const CACHE_PREFIX = "investhinks_cache_";
const DEFAULT_MAX_STALE_AGE = 60 * 60 * 1000; // 1 hour - data older than this won't be returned

interface CacheEntry {
  data: unknown;
  timestamp: number;
}

interface CacheResult<T = unknown> {
  data: T;
  timestamp: number;
  age: number;
}

/**
 * Crosses the unknown -> T type boundary at cache retrieval.
 * Data integrity is guaranteed because setToCache<T> accepts correctly-typed data,
 * so the runtime value matches the requested type T at retrieval time.
 */
function fromUnknown<T>(value: unknown): T {
  return value as T;
}

// In-memory fallback and session cache for faster reads
const memoryCache = new Map<string, CacheEntry>();

/**
 * Get the full storage key with prefix
 */
const getStorageKey = (key: string): string => `${CACHE_PREFIX}${key}`;

/**
 * Safely read from localStorage
 */
const readFromStorage = (key: string): CacheEntry | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(key));
    if (!stored) return null;
    const parsed: CacheEntry = JSON.parse(stored);
    return parsed;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : "Unknown error";
    console.warn(`[Cache] Failed to read from localStorage: ${message}`);
    return null;
  }
};

/**
 * Safely write to localStorage
 */
const writeToStorage = (key: string, value: CacheEntry): void => {
  try {
    localStorage.setItem(getStorageKey(key), JSON.stringify(value));
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : "Unknown error";
    console.warn(`[Cache] Failed to write to localStorage: ${message}`);
    // If quota exceeded, try to clear old cache entries
    if (e instanceof DOMException && e.name === "QuotaExceededError") {
      clearOldCacheEntries();
      try {
        localStorage.setItem(getStorageKey(key), JSON.stringify(value));
      } catch {
        // Give up on localStorage, just use memory
      }
    }
  }
};

/**
 * Remove from localStorage
 */
const removeFromStorage = (key: string): void => {
  try {
    localStorage.removeItem(getStorageKey(key));
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : "Unknown error";
    console.warn(`[Cache] Failed to remove from localStorage: ${message}`);
  }
};

/**
 * Clear old cache entries when quota is exceeded
 */
const clearOldCacheEntries = (): void => {
  try {
    const keysToRemove: string[] = [];
    const now = Date.now();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const storedRaw = localStorage.getItem(key);
          if (storedRaw) {
            const stored: CacheEntry = JSON.parse(storedRaw);
            // Remove entries older than 24 hours
            if (stored?.timestamp && now - stored.timestamp > 24 * 60 * 60 * 1000) {
              keysToRemove.push(key);
            }
          }
        } catch {
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : "Unknown error";
    console.warn(`[Cache] Failed to clear old entries: ${message}`);
  }
};

/**
 * Get cached data if it exists and is not too stale.
 */
export const getFromCache = <T = unknown>(
  key: string,
  maxStaleAge: number = DEFAULT_MAX_STALE_AGE
): CacheResult<T> | null => {
  // First check memory cache (faster)
  let cached: CacheEntry | null | undefined = memoryCache.get(key);

  // If not in memory, try localStorage
  if (!cached) {
    cached = readFromStorage(key);
    // Populate memory cache for faster subsequent reads
    if (cached) {
      memoryCache.set(key, cached);
    }
  }

  if (!cached) return null;

  const age = Date.now() - cached.timestamp;

  // If data is too old, don't return it (user will see loading state instead)
  if (age > maxStaleAge) {
    return null;
  }

  // Cache stores data as unknown - caller specifies T at the retrieval boundary.
  // Data was written via setToCache<T> which accepted the correctly-typed data,
  // so the runtime type matches T. We use an identity function to cross the boundary.
  const data: T = fromUnknown<T>(cached.data);
  return {
    data,
    timestamp: cached.timestamp,
    age,
  };
};

/**
 * Store data in cache with current timestamp.
 */
export const setToCache = <T = unknown>(key: string, data: T): void => {
  const entry: CacheEntry = {
    data,
    timestamp: Date.now(),
  };

  // Update both memory and localStorage
  memoryCache.set(key, entry);
  writeToStorage(key, entry);
};

/**
 * Clear cache entry or all entries.
 */
export const clearCache = (key?: string): void => {
  if (key) {
    memoryCache.delete(key);
    removeFromStorage(key);
  } else {
    // Clear all cache entries
    memoryCache.clear();
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey?.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(storageKey);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Unknown error";
      console.warn(`[Cache] Failed to clear localStorage: ${message}`);
    }
  }
};

/**
 * Get the age of a cache entry in milliseconds.
 */
export const getCacheAge = (key: string): number | null => {
  const cached = getFromCache(key, Infinity); // Get regardless of staleness for age check
  return cached ? cached.age : null;
};

/**
 * Check if cached data is considered stale (but still usable for SWR pattern).
 */
export const isCacheStale = (key: string, freshAge: number = 5 * 60 * 1000): boolean => {
  const age = getCacheAge(key);
  return age !== null && age > freshAge;
};
